import ClassNames from 'classnames'
import Counter from 'components/atoms/Counter'
import { range } from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../core/i18n'
import * as constants from '../../../static/constants'

import { Button } from '../../atoms'
import SvgIcon from 'components/atoms/SvgIcon'

interface IDay {
  date: moment.Moment
  blocked: boolean
  reserved: boolean
  price: number
  departures: any[]
}

interface IProps {
  data: IDay[]
  fetching: boolean
  isBokunData: boolean
  isAgentPrivateTour: boolean
  handleToggleClose(departure: any): void
  handleUpdateDeparture(departure: any): void
}

const CalendarGrid: React.FC<IProps> = props => {
  const { data, fetching, handleToggleClose, handleUpdateDeparture } = props
  const today = moment()
  const firstDayOfTheMonth = data[0].date
  const offsetCount = firstDayOfTheMonth.isoWeekday() % 7
  const cellCount = offsetCount + data.length
  const rowCount = Math.ceil(cellCount / 7)
  const cellData = [...range(offsetCount).map(() => null), ...data]
  const isMobile = matchMedia(`(max-width: ${constants.BREAKPOINT_TABLET_SMALL}px)`).matches
  const { isBokunData, isAgentPrivateTour } = props

  const [editItem, setEditItem] = React.useState(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  React.useEffect(() => {
    setEditItem(current => {
      if (!current) {
        return current
      }
      const changedDay = data.find(cell => cell.date.isSame(current.date))
      const changedDeparture = changedDay.departures.find(
        departure => departure.startTime === current.startTime
      )

      return {
        ...current,
        ...changedDeparture,
      }
    })
  }, [data])

  const currentMaxPeople = React.useMemo(() => {
    return editItem?.maxPeople || 0
  }, [data, editItem?.startTime])

  const handleMaxPeopleEdit = newValue => {
    setEditItem({
      ...editItem,
      maxPeople: newValue,
    })
  }

  const submit = async handleSubmit => {
    if (isSubmitting) {
      return
    }
    setIsSubmitting(true)
    try {
      await handleSubmit
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Grid>
      <Week>
        {constants.DAYS.map(day => (
          <div key={`week-${day.value}`}>{day.name}</div>
        ))}
      </Week>
      <Days>
        {range(rowCount).map((rowIndex: number) => (
          <GridRow key={`row-${rowIndex}`}>
            {range(7).map((dayIndex: number) => {
              const cellIndex = 7 * rowIndex + dayIndex
              const cell: null | IDay = cellData[cellIndex]
              const disabled = isBokunData && cell && moment(cell.date).isBefore(moment())

              return cell === null || cellIndex >= cellCount ? (
                <Empty key={`cell-${cellIndex}`} />
              ) : (
                <Day
                  key={`cell-${cellIndex}`}
                  grayOut={disabled}
                  isAgentPrivateTour={isAgentPrivateTour}
                >
                  <h2
                    className={ClassNames('Day_Index', { today: cell.date.isSame(today, 'day') })}
                  >
                    {`${cell.date.date()}${
                      cell.date.isSame(today, 'day') && !isMobile
                        ? `${I18n.t('generic.today')}`
                        : ''
                    }`}
                  </h2>
                  {cell.blocked && (
                    <svg
                      viewBox="0 0 150 150"
                      preserveAspectRatio="none"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <line x1="150" y1="0" x2="0" y2="150" stroke="#999" strokeWidth="1px" />
                    </svg>
                  )}
                  <div>
                    {disabled && (
                      <div className="pastDisabled">
                        BOKUN連携ツアーは過去在庫を取得できません。
                      </div>
                    )}
                    {cell.departures.map((departure, index) => (
                      <div
                        key={index}
                        className={`departure ${isBokunData && 'disabled'}`}
                        onClick={() =>
                          !isBokunData &&
                          setEditItem({
                            ...departure,
                            date: cell.date,
                          })
                        }
                      >
                        <span className="startTime">{departure.startTime}</span>
                        {departure.blocked ? (
                          <span className="closed">CLOSED</span>
                        ) : (
                          <div className="availability">
                            {(!isBokunData || !isAgentPrivateTour) && (
                              <div className="reservedItems">
                                <SvgIcon
                                  name="people"
                                  className="icon"
                                  ariaHidden={true}
                                  role="presentation"
                                />
                                <span className="reservedItemsNbers">
                                  {departure.reservedPeople} / {departure.maxPeople}
                                  {departure.reservedCount > 1 && (
                                    <span>x{departure.reservedCount}</span>
                                  )}
                                </span>
                              </div>
                            )}
                            {isAgentPrivateTour && (
                              <div className="reservedItems privateTickets">
                                <SvgIcon
                                  name="book_online"
                                  className="icon"
                                  ariaHidden={true}
                                  role="presentation"
                                />
                                <span className="reservedItemsNbers">
                                  {departure.reservedCount} / {departure.privateQuantity}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Day>
              )
            })}
          </GridRow>
        ))}
      </Days>
      {fetching && <OverLay />}

      {editItem && (
        <Modal>
          <div className="Modal_Panel">
            <h1 className="Modal_Title">Edit availability</h1>
            <div className="Modal_Info">
              <div className="Modal_InfoItem">
                <SvgIcon
                  name="calendar_today"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                <span>{editItem.date.format('YYYY/MM/DD')}</span>
              </div>
              <div className="Modal_InfoItem">
                <SvgIcon name="schedule" className="icon" ariaHidden={true} role="presentation" />
                <span>{editItem.startTime}</span>
              </div>
              <div className="Modal_InfoItem">
                <SvgIcon name="people" className="icon" ariaHidden={true} role="presentation" />
                <span>
                  {editItem.reservedPeople} / {currentMaxPeople}
                  {editItem.reservedCount > 1 && <span>x{editItem.reservedCount}</span>}
                </span>
              </div>
              {isAgentPrivateTour && (
                <div className="Modal_InfoItem">
                  <SvgIcon
                    name="book_online"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  <span>
                    {editItem.reservedCount} / {editItem.privateQuantity}
                  </span>
                </div>
              )}
            </div>
            <div>
              {editItem.blocked ? (
                <div className="Closed">
                  <span>This departure has been closed out</span>
                  <Button
                    handleClick={() => submit(handleToggleClose(editItem))}
                    disabled={isSubmitting}
                  >
                    Reopen
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="MaxPeople">
                    <div className="MaxPeople_Label">Max people</div>
                    <Counter defaultValue={editItem.maxPeople} handleChange={handleMaxPeopleEdit} />
                  </div>
                  <div className="Modal_Footer">
                    <Button
                      handleClick={() => submit(handleToggleClose(editItem))}
                      disabled={isSubmitting}
                    >
                      Closeout
                    </Button>
                    <Button
                      handleClick={() => submit(handleUpdateDeparture(editItem))}
                      disabled={isSubmitting}
                    >
                      {editItem.postCalendarId ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <span className="Modal_Close" onClick={() => setEditItem(null)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                aria-hidden="true"
                role="presentation"
              >
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                <path d="M0 0h24v24H0z" fill="none" />
              </svg>
            </span>
          </div>
        </Modal>
      )}
    </Grid>
  )
}

const Modal = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.24);
  z-index: 5000;

  .Closed {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 24px;

    > span {
      font-weight: bold;
      color: ${constants.COLORS.cautionText};
    }
  }

  .MaxPeople {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 24px;
  }

  .MaxPeople_Label {
    font-size: 16px;
    font-weight: bold;
  }

  .MaxPeople_Control {
    display: flex;
    align-items: center;

    .control {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 40px;
      height: 40px;
      cursor: pointer;
      border-radius: 50%;
      border: solid 1px ${constants.COLORS.divider};

      :hover {
        border: solid 1px ${constants.COLORS.primary};
      }
    }

    .number {
      display: block;
      width: 32px;
      text-align: center;
      margin: 0 20px;
    }
  }

  .Modal_Footer {
    margin-top: 32px;
  }

  .Modal_Info {
    display: flex;
    margin-top: 18px;
    padding-bottom: 12px;
    border-bottom: solid 1px ${constants.COLORS.divider};
  }

  .Modal_InfoItem {
    display: flex;
    align-items: center;

    .icon {
      font-size: 20px;
      margin-right: 8px;
    }
  }

  .Modal_InfoItem + .Modal_InfoItem {
    margin-left: 32px;
  }

  .Modal_Panel {
    position: relative;
    width: 100%;
    max-width: 600px;
    padding: 24px;
    background-color: ${constants.COLORS.white};
    border-radius: 8px;

    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: 92%;
    }
  }

  .Modal_Title {
    font-size: 24px;
  }

  .Modal_Close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 24px;
    height: 24px;
    cursor: pointer;
  }
`

const Grid = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const GridRow = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  width: 100%;

  .reservedItems {
    .icon {
      vertical-align: bottom;
    }
    .reservedItemsNbers {
      margin-left: 5px;
    }
  }
`

const Week = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-bottom: solid 1px ${constants.COLORS.divider};

  > div {
    display: flex;
    justify-content: center;
    width: 14.28%;
    height: 20px;
    font-size: 13px;
  }
`

const Days = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: -1px;
`

const Empty = styled.span`
  display: flex;
  width: 14.28%;
  padding: 8px;
  border-bottom: solid 1px ${constants.COLORS.divider};

  &:last-of-type {
    border-right: solid 1px ${constants.COLORS.divider};
  }
`

const Day = styled.div<{ grayOut: boolean; isAgentPrivateTour: boolean }>`
  position: relative;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  width: 14.28%;
  padding: 8px;
  border-right: solid 1px ${constants.COLORS.divider};
  border-bottom: solid 1px ${constants.COLORS.divider};
  cursor: pointer;
  transition: opacity 0.1s ease;
  overflow: auto;
  background-color: ${props => (props.grayOut ? 'lightgray' : '')};

  .Day_Index {
    margin-bottom: 4px;
  }

  .departure {
    display: flex;
    justify-content: space-between;
    padding: 2px;
    font-size: 12px;
    border-top: solid 1px ${constants.COLORS.divider};
    flex-direction: ${props => (props.isAgentPrivateTour ? 'column' : '')};
    align-items: center;

    .closed {
      font-weight: bold;
      margin-left: auto;
    }

    :hover {
      background-color: ${constants.COLORS.divider};
    }

    :last-child {
      border-bottom: solid 1px ${constants.COLORS.divider};
    }
  }

  .departure.disabled {
    :hover {
      background-color: white;
    }
  }

  &.blocked {
    background-color: ${constants.COLORS.disabled};
  }

  &.reserved {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${constants.COLORS.primary};
      opacity: 0.2;
    }
  }

  &.selected {
    padding: 6px 8px 7px 8px;
    border-top: solid 2px ${constants.COLORS.primary};
    border-bottom: solid 2px ${constants.COLORS.primary};

    &.start {
      padding: 6px 8px 7px 6px;
      border-left: solid 2px ${constants.COLORS.primary};
    }

    &.end {
      padding: 6px 7px 7px 8px;
      border-right: solid 2px ${constants.COLORS.primary};
    }

    &.start.end {
      padding: 6px 7px 7px 6px;
    }
  }

  .hasSelectedDate & {
    &:not(.selected) {
      opacity: 0.5;
    }
  }

  > h2 {
    margin: 0;
    font-weight: normal;
    font-size: 14px;

    &.today {
      font-weight: bold;
      color: ${constants.COLORS.primary};
    }
  }

  > p {
    margin: 0;
    text-align: right;
    font-size: 14px;
  }

  > svg {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
  }

  .pastDisabled {
    color: gray;
    font-size: 8px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      display: none;
    }
  }

  .availability {
    display: flex;
    width: 100%;
    justify-content: end;
  }

  .startTime {
    margin-right: auto;
  }
`

const OverLay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.6);
  z-index: 9999;
`

export default CalendarGrid
